// Painel de emojis, GIFs e figurinhas das Mensagens (0.51), no lugar do teclado (como no WhatsApp).
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Alert, FlatList, Image, Modal, Pressable, ScrollView, Text, TextInput, View, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { C, alpha } from './theme';
import { Chip } from './ui';
import { FigurinhaView } from './Figurinha';
import { useDoc } from '../store/store';
import type { AnimFig, Fig, GifSalvo } from '../store/types';
import { t } from '../lib/i18n';
import {
  ANIMACOES, CATEGORIAS_EMOJI, CORES_FIG, PRONTAS, apagarFigurinha, apagarGif, buscarEmojis, buscarKlipy, emojisDa, guardarFigurinha, guardarGif,
  mudarPele, temKlipy, usarEmoji, ANIMADOS, figurinhaAnimada, TEMAS_GIF, type Emoji, type ItemKlipy,
} from '../lib/figurinhas';
import { enviarFigurinha, enviarGif, subirFotoFigurinha } from '../lib/chat';
import { escolherFotos, lerBytes, tamanhoDe, temTratamento } from '../lib/scanArquivos';
import { ROLAGEM_TECLADO } from './teclado';

type Aba = 'emoji' | 'gif' | 'fig';
const falha = (e: any) => Alert.alert(t('Não foi possível enviar'), String(e?.message ?? e));

export function PainelExpressao({ conv, cor, onEmoji, onEnviado }: { conv: string; cor: string; onEmoji: (e: string) => void; onEnviado?: () => void }) {
  const ins = useSafeAreaInsets();
  const [aba, setAba] = useState<Aba>('emoji');
  return (
    <View style={{ height: 340 + ins.bottom, backgroundColor: C.card, borderTopWidth: 1, borderTopColor: C.line, paddingBottom: ins.bottom }}>
      <View style={{ flexDirection: 'row', alignSelf: 'center', backgroundColor: C.card2, borderRadius: 20, padding: 3, marginVertical: 8 }}>
        {([['emoji', '😊', t('Emojis')], ['gif', 'GIF', 'GIF'], ['fig', '🎉', t('Figurinhas')]] as [Aba, string, string][]).map(([id, ic, nome]) => (
          <Pressable key={id} onPress={() => setAba(id)} accessibilityLabel={nome}
            style={{ paddingHorizontal: 18, paddingVertical: 6, borderRadius: 16, backgroundColor: aba === id ? cor : 'transparent' }}>
            <Text style={{ color: aba === id ? '#fff' : C.sub, fontWeight: '800', fontSize: id === 'gif' ? 13 : 16 }}>{ic}</Text>
          </Pressable>
        ))}
      </View>
      {aba === 'emoji' && <Emojis cor={cor} onEmoji={onEmoji} onAnimado={e => { enviarFigurinha(conv, figurinhaAnimada(e)).catch(falha); onEnviado?.(); }} />}
      {aba === 'gif' && <Gifs conv={conv} cor={cor} onEnviado={onEnviado} />}
      {aba === 'fig' && <Figurinhas conv={conv} cor={cor} onEnviado={onEnviado} />}
    </View>
  );
}

// ─── emojis ───
const Celula = memo(function Celula({ x, onPress, onLong }: { x: Emoji; onPress: (e: string) => void; onLong: (x: Emoji) => void }) {
  return (
    <Pressable onPress={() => onPress(x.e)} onLongPress={() => x.peles && onLong(x)} delayLongPress={300}
      style={{ flex: 1, aspectRatio: 1, alignItems: 'center', justifyContent: 'center' }}>
      <Text style={{ fontSize: 28 }}>{x.e}</Text>
      {!!x.peles && <Text style={{ position: 'absolute', right: 3, bottom: 1, color: C.mute, fontSize: 8 }}>◢</Text>}
    </Pressable>
  );
});
function Emojis({ cor, onEmoji, onAnimado }: { cor: string; onEmoji: (e: string) => void; onAnimado?: (e: string) => void }) {
  const { emojis: recentes = [], pele = 0 } = useDoc('figurinhas');
  const [cat, setCat] = useState(recentes.length ? -1 : 0);
  const [q, setQ] = useState('');
  const [tons, setTons] = useState<Emoji | null>(null);
  const ANIM = 99;   // categoria dos emojis animados
  const lista = useMemo(() => (q.trim() ? buscarEmojis(q, pele) : cat < 0 ? recentes.map(e => ({ e })) : cat === ANIM ? ANIMADOS.map(e => ({ e })) : emojisDa(cat, pele)), [q, cat, pele, recentes]);
  const ref = useRef<FlatList>(null);
  const tocar = (e: string) => { if (cat === ANIM && !q && onAnimado) { onAnimado(e); return; } onEmoji(e); usarEmoji(e); };
  return (
    <View style={{ flex: 1 }}>
      <View style={{ paddingHorizontal: 10, paddingBottom: 6 }}>
        <TextInput value={q} onChangeText={setQ} placeholder={'🔎 ' + t('Buscar emoji (ex.: coração, risada)')} placeholderTextColor={C.mute}
          style={{ color: C.text, backgroundColor: C.card2, borderRadius: 14, paddingHorizontal: 12, paddingVertical: 8 }} />
      </View>
      {cat === ANIM && !q && <Text style={{ color: cor, fontSize: 12, textAlign: 'center', marginBottom: 4 }}>✨ {t('Toque para enviar o emoji animado')}</Text>}
      <FlatList ref={ref} data={lista} numColumns={8} keyExtractor={(x, i) => x.e + i} initialNumToRender={64} windowSize={7}
        renderItem={({ item }) => <Celula x={item} onPress={tocar} onLong={setTons} />}
        ListEmptyComponent={<Text style={{ color: C.mute, textAlign: 'center', marginTop: 30 }}>{cat < 0 && !q ? t('Os emojis que você usar aparecem aqui.') : t('Nada encontrado.')}</Text>}
        contentContainerStyle={{ paddingHorizontal: 6 }} />
      {!q && (
        <View style={{ flexDirection: 'row', justifyContent: 'space-around', paddingVertical: 4, borderTopWidth: 1, borderTopColor: C.line }}>
          <Pressable onPress={() => { setCat(-1); ref.current?.scrollToOffset({ offset: 0, animated: false }); }} style={{ padding: 6, borderRadius: 12, backgroundColor: cat < 0 ? alpha(cor, 0.25) : 'transparent' }}><Text style={{ fontSize: 18 }}>🕘</Text></Pressable>
          {!!onAnimado && <Pressable accessibilityLabel={t('Emojis animados')} onPress={() => { setCat(ANIM); ref.current?.scrollToOffset({ offset: 0, animated: false }); }}
            style={{ padding: 6, borderRadius: 12, backgroundColor: cat === ANIM ? alpha(cor, 0.25) : 'transparent' }}><Text style={{ fontSize: 18 }}>✨</Text></Pressable>}
          {CATEGORIAS_EMOJI.map((c, i) => (
            <Pressable key={i} accessibilityLabel={t(c.nome)} onPress={() => { setCat(i); ref.current?.scrollToOffset({ offset: 0, animated: false }); }}
              style={{ padding: 6, borderRadius: 12, backgroundColor: cat === i ? alpha(cor, 0.25) : 'transparent' }}><Text style={{ fontSize: 18 }}>{c.icone}</Text></Pressable>
          ))}
        </View>
      )}
      {/* tons de pele: segure o emoji */}
      <Modal visible={!!tons} transparent animationType="fade" onRequestClose={() => setTons(null)}>
        <Pressable onPress={() => setTons(null)} style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'center', alignItems: 'center' }}>
          <View style={{ backgroundColor: C.card, borderRadius: 18, padding: 10, flexDirection: 'row', gap: 4 }}>
            {(tons?.peles ?? []).map((e, i) => (
              <Pressable key={e} onPress={() => { mudarPele(i); tocar(e); setTons(null); }} style={{ padding: 8, borderRadius: 12, backgroundColor: i === pele ? alpha(cor, 0.25) : 'transparent' }}>
                <Text style={{ fontSize: 30 }}>{e}</Text>
              </Pressable>
            ))}
          </View>
          <Text style={{ color: '#fff', marginTop: 10, fontSize: 12 }}>{t('O tom escolhido fica para os próximos emojis.')}</Text>
        </Pressable>
      </Modal>
    </View>
  );
}

// ─── GIFs ───
function GradeKlipy({ itens, carregando, onTocar, onSegurar, rodape }: { itens: (ItemKlipy | GifSalvo)[]; carregando: boolean; onTocar: (x: ItemKlipy | GifSalvo) => void; onSegurar?: (x: ItemKlipy | GifSalvo) => void; rodape?: React.ReactNode }) {
  const { width } = useWindowDimensions();
  const larg = (Math.min(width, 700) - 24) / 2;
  return (
    <FlatList data={itens} numColumns={2} keyExtractor={(x, i) => x.id + i} contentContainerStyle={{ paddingHorizontal: 8, gap: 6, paddingBottom: 10 }} columnWrapperStyle={{ gap: 6 }}
      renderItem={({ item }) => (
        <Pressable onPress={() => onTocar(item)} onLongPress={() => onSegurar?.(item)} delayLongPress={350}
          style={{ width: larg, height: 120, borderRadius: 10, overflow: 'hidden', backgroundColor: C.card2 }}>
          <Image source={{ uri: 'prev' in item ? item.prev : item.url }} style={{ width: '100%', height: '100%' }} resizeMode="cover" />
        </Pressable>
      )}
      ListEmptyComponent={carregando ? <ActivityIndicator style={{ marginTop: 30 }} /> : <Text style={{ color: C.mute, textAlign: 'center', marginTop: 30 }}>{t('Nada encontrado.')}</Text>}
      ListFooterComponent={<>{rodape}</>} />
  );
}
function useBuscaKlipy(tipo: 'gifs' | 'stickers', q: string, ligado: boolean) {
  const [itens, setItens] = useState<ItemKlipy[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  useEffect(() => {
    if (!ligado || !temKlipy()) return;
    let vivo = true;
    setCarregando(true); setErro('');
    const tm = setTimeout(() => buscarKlipy(tipo, q).then(r => { if (vivo) setItens(r.itens); }).catch(e => { if (vivo) { setItens([]); setErro(String(e?.message ?? e)); } }).finally(() => { if (vivo) setCarregando(false); }), q ? 450 : 0);
    return () => { vivo = false; clearTimeout(tm); };
  }, [tipo, q, ligado]);
  return { itens, carregando, erro };
}
const Klipy = () => <Text style={{ color: C.mute, fontSize: 10, textAlign: 'center', marginTop: 6 }}>Powered by KLIPY</Text>;

function Gifs({ conv, cor, onEnviado }: { conv: string; cor: string; onEnviado?: () => void }) {
  const { gifs: meus = [] } = useDoc('figurinhas');
  const [q, setQ] = useState('');
  const [modo, setModo] = useState<'alta' | 'meus'>('alta');
  const busca = useBuscaKlipy('gifs', q, modo === 'alta');
  const enviar = (g: ItemKlipy | GifSalvo) => { enviarGif(conv, { url: g.url, w: g.w, h: g.h }).catch(falha); onEnviado?.(); };
  return (
    <View style={{ flex: 1 }}>
      <View style={{ paddingHorizontal: 10, paddingBottom: 6, gap: 6 }}>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <Chip small cor={cor} on={modo === 'alta'} label={'🔥 ' + t('Em alta')} onPress={() => setModo('alta')} />
          <Chip small cor={cor} on={modo === 'meus'} label={'⭐ ' + t('Meus GIFs ({n})', { n: meus.length })} onPress={() => setModo('meus')} />
        </View>
        {modo === 'alta' && temKlipy() && (
          <>
            <TextInput value={q} onChangeText={setQ} placeholder={'🔎 ' + t('Buscar GIF (ex.: parabéns, bom dia)')} placeholderTextColor={C.mute}
              style={{ color: C.text, backgroundColor: C.card2, borderRadius: 14, paddingHorizontal: 12, paddingVertical: 8 }} />
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 6 }}>
              {TEMAS_GIF.map(x => <Chip key={x} small cor={cor} on={q === t(x)} label={t(x)} onPress={() => setQ(q === t(x) ? '' : t(x))} />)}
            </ScrollView>
          </>
        )}
      </View>
      {modo === 'meus' ? (
        <GradeKlipy itens={meus} carregando={false} onTocar={enviar}
          onSegurar={g => Alert.alert(t('Tirar dos meus GIFs?'), '', [{ text: t('Cancelar'), style: 'cancel' }, { text: t('Tirar'), style: 'destructive', onPress: () => apagarGif(g.id) }])}
          rodape={!meus.length ? <Text style={{ color: C.mute, textAlign: 'center', marginTop: 30, paddingHorizontal: 20 }}>{t('Segure um GIF numa conversa e toque em "Guardar" para ele aparecer aqui.')}</Text> : null} />
      ) : !temKlipy() ? (
        <Text style={{ color: C.sub, textAlign: 'center', marginTop: 30, paddingHorizontal: 24 }}>{t('A busca de GIFs ainda não foi ligada neste app. Enquanto isso, use as figurinhas animadas (🎉).')}</Text>
      ) : (
        <>
          {!!busca.erro && <Text style={{ color: C.danger, textAlign: 'center', fontSize: 12 }}>{t('Sem conexão com os GIFs agora.')}</Text>}
          <GradeKlipy itens={busca.itens} carregando={busca.carregando} onTocar={enviar}
            onSegurar={g => { guardarGif({ url: g.url, w: g.w, h: g.h }); Alert.alert('⭐', t('GIF guardado nos seus GIFs.')); }} rodape={<Klipy />} />
        </>
      )}
    </View>
  );
}

// ─── figurinhas ───
function Figurinhas({ conv, cor, onEnviado }: { conv: string; cor: string; onEnviado?: () => void }) {
  const { minhas = [] } = useDoc('figurinhas');
  const { width } = useWindowDimensions();
  const [criar, setCriar] = useState<'emoji' | 'foto' | null>(null);
  const [q, setQ] = useState('');
  const [klipy, setKlipy] = useState(false);
  const [todosAnim, setTodosAnim] = useState(false);
  const busca = useBuscaKlipy('stickers', q, klipy);
  const col = width > 700 ? 8 : 4;
  const tam = Math.floor((Math.min(width, 900) - 24) / col) - 6;
  const enviar = (f: Omit<Fig, 'id'> & { id?: string }) => { enviarFigurinha(conv, f).catch(falha); onEnviado?.(); };
  const Grade = ({ fs, onSegurar }: { fs: (Omit<Fig, 'id'> & { id?: string })[]; onSegurar?: (f: any) => void }) => (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
      {fs.map((f, i) => (
        <Pressable key={(f.id ?? '') + i} onPress={() => enviar(f)} onLongPress={() => onSegurar?.(f)} delayLongPress={350}
          style={{ width: tam, height: tam, borderRadius: 12, backgroundColor: C.card2, alignItems: 'center', justifyContent: 'center' }}>
          <FigurinhaView f={f} tam={tam - 6} animar={false} />
        </Pressable>
      ))}
    </View>
  );
  return (
    <ScrollView {...ROLAGEM_TECLADO} contentContainerStyle={{ paddingHorizontal: 10, paddingBottom: 16, gap: 10 }} keyboardShouldPersistTaps="handled">
      <View style={{ flexDirection: 'row', gap: 8 }}>
        <Pressable onPress={() => setCriar('emoji')} style={{ flex: 1, backgroundColor: cor, borderRadius: 14, padding: 10, alignItems: 'center' }}>
          <Text style={{ color: '#fff', fontWeight: '800' }}>✨ {t('Criar animada')}</Text>
        </Pressable>
        <Pressable onPress={() => setCriar('foto')} style={{ flex: 1, backgroundColor: alpha(cor, 0.2), borderRadius: 14, padding: 10, alignItems: 'center' }}>
          <Text style={{ color: C.text, fontWeight: '800' }}>📷 {t('De uma foto')}</Text>
        </Pressable>
      </View>
      {!!minhas.length && <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700' }}>⭐ {t('Minhas figurinhas')}</Text>}
      {!!minhas.length && <Grade fs={minhas} onSegurar={f => Alert.alert(t('Tirar das minhas figurinhas?'), '', [{ text: t('Cancelar'), style: 'cancel' }, { text: t('Tirar'), style: 'destructive', onPress: () => apagarFigurinha(f.id) }])} />}
      <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700' }}>✨ {t('Emojis animados')}</Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
        {(todosAnim ? ANIMADOS : ANIMADOS.slice(0, col * 3)).map(e => (
          <Pressable key={e} onPress={() => enviar(figurinhaAnimada(e))} onLongPress={() => { guardarFigurinha(figurinhaAnimada(e)); Alert.alert('⭐', t('Guardada nas suas figurinhas.')); }}
            style={{ width: (tam - 6) / 1.4, height: (tam - 6) / 1.4, alignItems: 'center', justifyContent: 'center' }}>
            <Text style={{ fontSize: tam / 2.6 }}>{e}</Text>
          </Pressable>
        ))}
      </View>
      {!todosAnim && <Pressable onPress={() => setTodosAnim(true)}><Text style={{ color: cor, fontSize: 13, fontWeight: '700' }}>{t('Ver todos ({n})', { n: ANIMADOS.length })}</Text></Pressable>}
      <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700' }}>🎉 {t('Prontas')}</Text>
      <Grade fs={PRONTAS.map(p => ({ ...p, t: t(p.t) }))} onSegurar={f => { guardarFigurinha(f); Alert.alert('⭐', t('Guardada nas suas figurinhas.')); }} />
      {temKlipy() && (
        <>
          <Pressable onPress={() => setKlipy(v => !v)}><Text style={{ color: cor, fontSize: 13, fontWeight: '700' }}>{klipy ? '▾' : '▸'} {t('Mais figurinhas animadas (KLIPY)')}</Text></Pressable>
          {klipy && (
            <>
              <TextInput value={q} onChangeText={setQ} placeholder={'🔎 ' + t('Buscar figurinha')} placeholderTextColor={C.mute}
                style={{ color: C.text, backgroundColor: C.card2, borderRadius: 14, paddingHorizontal: 12, paddingVertical: 8 }} />
              {busca.carregando && <ActivityIndicator color={cor} />}
              <Grade fs={busca.itens.map(x => ({ id: x.id, a: 'nada' as AnimFig, url: x.url, w: x.w, h: x.h }))} onSegurar={f => { guardarFigurinha({ a: 'nada', url: f.url, w: f.w, h: f.h }); Alert.alert('⭐', t('Guardada nas suas figurinhas.')); }} />
              <Klipy />
            </>
          )}
        </>
      )}
      <Text style={{ color: C.mute, fontSize: 11, textAlign: 'center' }}>{t('Toque para enviar. Segure para guardar ou tirar das suas.')}</Text>
      <Text style={{ color: C.mute, fontSize: 10, textAlign: 'center' }}>{t('Emojis animados: Noto, do Google (CC BY 4.0).')}</Text>
      <CriarFigurinha modo={criar} conv={conv} cor={cor} onFechar={() => setCriar(null)} onEnviar={f => { setCriar(null); guardarFigurinha(f); enviar(f); }} />
    </ScrollView>
  );
}

// ─── criar figurinha ───
function CriarFigurinha({ modo, conv, cor, onFechar, onEnviar }: { modo: 'emoji' | 'foto' | null; conv: string; cor: string; onFechar: () => void; onEnviar: (f: Omit<Fig, 'id'>) => void }) {
  const ins = useSafeAreaInsets();
  const [f, setF] = useState<Omit<Fig, 'id'>>({ e: '😎', a: 'pulo', t: '' });
  const [escolhendo, setEscolhendo] = useState(false);
  const [subindo, setSubindo] = useState(false);
  useEffect(() => {
    if (modo === 'emoji') setF({ e: '😎', a: 'pulo', t: '' });
    if (modo === 'foto') { setF({ a: 'pulsa', t: '' }); escolherFoto(); }
  }, [modo]); // eslint-disable-line react-hooks/exhaustive-deps

  async function escolherFoto() {
    try {
      const [uri] = await escolherFotos();
      if (!uri) { if (!f.img) onFechar(); return; }
      setSubindo(true);
      let bytes: Uint8Array;
      if (temTratamento()) {
        // recorta o meio da foto num quadrado de 512 px
        const IM = require('expo-image-manipulator');
        const { w, h } = await tamanhoDe(uri);
        const s = Math.min(w, h);
        const r = await IM.manipulateAsync(uri, [{ crop: { originX: Math.round((w - s) / 2), originY: Math.round((h - s) / 2), width: s, height: s } }, { resize: { width: 512 } }],
          { compress: 0.8, format: IM.SaveFormat.JPEG });
        bytes = await lerBytes(r.uri);
      } else {
        bytes = await lerBytes(uri);
        if (bytes.length > 2 * 1024 * 1024) throw new Error(t('A foto é grande demais. Instale o app novo para recortar a foto.'));
      }
      const img = await subirFotoFigurinha(conv, bytes);
      setF(x => ({ ...x, img, e: undefined }));
    } catch (e) { falha(e); if (!f.img) onFechar(); } finally { setSubindo(false); }
  }

  return (
    <Modal visible={!!modo} animationType="slide" onRequestClose={onFechar}>
      <View style={{ flex: 1, backgroundColor: C.bg, paddingTop: ins.top + 6, paddingBottom: ins.bottom + 10 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 10 }}>
          <Pressable onPress={onFechar} hitSlop={12}><Text style={{ color: C.sub, fontSize: 15 }}>{t('Cancelar')}</Text></Pressable>
          <Text style={{ flex: 1, textAlign: 'center', color: C.text, fontWeight: '700', fontSize: 16 }}>{t('Criar figurinha')}</Text>
          <Pressable disabled={subindo || (modo === 'foto' && !f.img)} onPress={() => onEnviar(f)} hitSlop={12}><Text style={{ color: cor, fontWeight: '800', fontSize: 15 }}>{t('Enviar')}</Text></Pressable>
        </View>
        <ScrollView {...ROLAGEM_TECLADO} contentContainerStyle={{ padding: 16, gap: 12, alignItems: 'stretch' }} keyboardShouldPersistTaps="handled">
          <View style={{ alignItems: 'center', backgroundColor: C.card, borderRadius: 20, paddingVertical: 16 }}>
            {subindo ? <ActivityIndicator color={cor} style={{ height: 200 }} /> : <FigurinhaView f={f} tam={200} />}
          </View>
          {modo === 'emoji' ? (
            <Pressable onPress={() => setEscolhendo(true)} style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <Text style={{ fontSize: 30 }}>{f.e}</Text><Text style={{ color: C.text, flex: 1 }}>{t('Trocar o emoji')}</Text><Text style={{ color: C.mute }}>›</Text>
            </Pressable>
          ) : (
            <Pressable onPress={escolherFoto} style={{ backgroundColor: C.card, borderRadius: 14, padding: 12 }}><Text style={{ color: C.text }}>📷 {t('Trocar a foto')}</Text></Pressable>
          )}
          <TextInput value={f.t} onChangeText={v => setF(x => ({ ...x, t: v.slice(0, 30) }))} placeholder={t('Frase (opcional, ex.: Bom dia!)')} placeholderTextColor={C.mute}
            style={{ color: C.text, backgroundColor: C.card, borderRadius: 14, padding: 12, fontSize: 16 }} />
          <Text style={{ color: C.sub, fontSize: 12 }}>{t('Animação')}</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
            {ANIMACOES.map(a => <Chip key={a.id} small cor={cor} on={f.a === a.id} label={`${a.icone} ${t(a.nome)}`} onPress={() => setF(x => ({ ...x, a: a.id }))} />)}
          </View>
          {modo === 'emoji' && (
            <>
              <Text style={{ color: C.sub, fontSize: 12 }}>{t('Fundo')}</Text>
              <View style={{ flexDirection: 'row', gap: 10 }}>
                {CORES_FIG.map(c => (
                  <Pressable key={c || 'sem'} onPress={() => setF(x => ({ ...x, c: c || undefined }))}
                    style={{ width: 34, height: 34, borderRadius: 17, backgroundColor: c || 'transparent', borderWidth: (f.c ?? '') === c ? 3 : 1, borderColor: (f.c ?? '') === c ? cor : C.line, alignItems: 'center', justifyContent: 'center' }}>
                    {!c && <Text style={{ color: C.mute }}>∅</Text>}
                  </Pressable>
                ))}
              </View>
            </>
          )}
          <Text style={{ color: C.mute, fontSize: 11 }}>{t('Ela também fica guardada em "Minhas figurinhas" para mandar de novo.')}</Text>
        </ScrollView>
        <Modal visible={escolhendo} animationType="slide" onRequestClose={() => setEscolhendo(false)}>
          <View style={{ flex: 1, backgroundColor: C.bg, paddingTop: ins.top + 6 }}>
            <Pressable onPress={() => setEscolhendo(false)} style={{ padding: 16 }}><Text style={{ color: C.sub }}>{t('Fechar')}</Text></Pressable>
            <Emojis cor={cor} onEmoji={e => { setF(x => ({ ...x, e })); setEscolhendo(false); }} />
          </View>
        </Modal>
      </View>
    </Modal>
  );
}
